<template>
  <table class="w-full">
    <thead class="sticky top-0 z-20 bg-white">
      <tr class="flex w-full">
        <th class="sticky left-0 z-30 flex items-center justify-center flex-shrink-0 w-12 p-2 overflow-hidden text-sm font-bold text-center border-b border-r border-solid sm:w-40 border-neutral-200 h-11 bg-neutral-100 text-neutral-600">Bruger</th>
        <th v-for="eventType in eventTypesWithBalance" :key="eventType" 
            class="flex items-center justify-center flex-shrink-0 w-20 p-2 overflow-hidden text-sm font-bold text-center border-b border-r border-solid sm:w-36 border-neutral-200 h-11 bg-neutral-100 text-neutral-600">
          <span class="truncate" :title="eventType">{{ eventType }}</span>
        </th>
        <th class="flex items-center justify-end flex-grow p-2 text-sm font-bold text-center border-b border-solid border-neutral-200 h-11 bg-neutral-100"></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="user in users" :key="user.id" class="flex group hover:bg-neutral-50">
        <td
          class="sticky left-0 z-10 flex-shrink-0 w-12 text-sm bg-white border-b border-r border-solid cursor-pointer sm:w-40 group-hover:bg-neutral-50 sticky-column border-neutral-200"
          @mouseover="showUserInfo(user, $event)" @mouseleave="$emit('hide-user-info')">
          <div class="px-2 w-full h-full overflow-hidden text-ellipsis whitespace-nowrap leading-[2.75rem]"
            :title="user.first_name + ' ' + user.last_name">
            <span class="sm:hidden">{{ usersStore.getUserInitials(user) }}</span>
            <span class="hidden sm:inline">{{ user.first_name + " " + user.last_name }}</span>
          </div>
        </td>
        <td v-for="eventType in eventTypesWithBalance" :key="eventType"
          class="flex items-center flex-shrink-0 w-20 p-2 text-sm border-b border-r border-solid sm:w-36 border-neutral-200">
          <div class="flex items-center justify-between w-full">
            <div class="flex-shrink-0">
              <CheckCircle2 v-if="getUserBalance(user, eventType) === 0" class="text-green-500" :size="16" />
              <XCircle v-else class="text-red-500" :size="16" />
            </div>
            <span class="flex-grow text-lg font-bold text-center">
              {{ getUserBalance(user, eventType) }}
            </span>
            <span class="flex-shrink-0 text-center text-md text-neutral-500">
              ({{ getUserNextYearBalance(user, eventType) }})
            </span>
          </div>
        </td>
        <td class="flex items-center justify-end flex-grow p-2 border-b border-solid border-neutral-200">
          <button @click="openEditModal(user)"
            class="h-full px-3.5 text-sm font-medium border-none rounded-md text-neutral-800 bg-neutral-100 hover:bg-neutral-200 focus:outline-none">
            Rediger
          </button>
        </td>
      </tr>
    </tbody>
  </table>

  <ReusableModal :isOpen="showEditModal" :isConfirm="true" @close="closeEditModal" @submit="updateUserBalance"
    title="Rediger brugersaldo">
    <div v-if="selectedUser">
      <p class="mb-4 text-sm text-neutral-800">
        Denne funktion er kun en fejlsikring, hvis der er noget der skulle gå galt. Brug den kun hvis du er sikker på at
        du vil redigere saldoen.
      </p>
      <h3 class="mb-4 font-semibold text-md text-neutral-800">Bruger: {{ selectedUser.first_name }} {{
        selectedUser.last_name }}</h3>
      <div v-for="eventType in eventTypesWithBalance" :key="eventType" class="mb-4">
        <label :for="eventType" class="block mb-1.5 text-sm font-medium text-gray-900">{{ eventType }}</label>
        <input :id="eventType" v-model="editedBalances[eventType]" type="number"
          class="bg-neutral-50 border border-neutral-300 border-solid text-neutral-900 text-sm rounded-lg focus:ring-neutral-500 focus:border-neutral-500 block w-full p-2.5" />
      </div>
    </div>
  </ReusableModal>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { CheckCircle2, XCircle } from 'lucide-vue-next';
import ReusableModal from '@/components/ui/ReusableModal.vue';
import { useEventsStore } from '@/stores/eventsStore';
import { useUsersStore } from '@/stores/usersStore';

defineProps({
  users: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['show-user-info', 'hide-user-info']);

const eventsStore = useEventsStore();
const usersStore = useUsersStore();

const eventTypesWithBalance = computed(() => eventsStore.getEventTypesWithBalance);

const showEditModal = ref(false);
const selectedUser = ref(null);
const editedBalances = ref({});

function getUserBalance(user, eventType) {
  const balance = user.holiday_balances.find(b => b.type === eventType);
  return balance ? balance.balance : 0;
}

function getUserNextYearBalance(user, eventType) {
  const balance = user.holiday_balances.find(b => b.type === eventType);
  return balance ? balance.next_year_balance : 0;
}

function openEditModal(user) {
  selectedUser.value = user;
  editedBalances.value = {};
  eventTypesWithBalance.value.forEach(eventType => {
    editedBalances.value[eventType] = getUserBalance(user, eventType);
  });
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  selectedUser.value = null;
  editedBalances.value = {};
}

function showUserInfo(user, event) {
  emit('show-user-info', user, event.clientX, event.clientY);
}

async function updateUserBalance() {
  if (selectedUser.value) {
    const updatedBalances = Object.entries(editedBalances.value).map(([type, balance]) => ({
      type,
      balance: parseInt(balance, 10),
    }));

    const success = usersStore.updateUserBalances(selectedUser.value.id, updatedBalances);
    if (success) {
      usersStore.fetchUsers();
      closeEditModal();
    } else {
      console.error('Failed to update user balances');
    }
  }
}
</script>